// ============================================================================
// AXIOM — TRYPORTAL integration
// ----------------------------------------------------------------------------
// The whole platform surface lives in this one module: the session handshake,
// the leaderboard submission, the five live actions a viewer can fire at a
// running game, and the free tier's raw events. Everything else in the game is
// reachable only through S and the bus, so ripping this file out returns the
// game to a stock build.
//
// Two rules the handlers follow everywhere:
//   - The manifest's defaultLimits are the creator's STARTING values, not a
//     defence — the platform lets a rule exceed most of them. Every handler
//     clamps its own numbers.
//   - Every action must be visible within the same frame it applies. A viewer
//     is watching a stream with seconds of latency; an effect they cannot
//     connect to their own gift is not interaction.
// ============================================================================

import S from './Registry.js';
import { bus } from './EventBus.js';
import { connect, connectStandalone, defineActions, defineBindings, guardText, isTRYPORTALError } from '../tryportal/index.js';
import manifest from '../../manifest.json';
import { BINDING_DECLS } from './PortalBindings.js';
import { rollToken } from '../math/Ops.js';
import { rand } from './Rand.js';
import { PortalRefreshQueue } from './PortalRefreshQueue.js';
import { createRunScoreProof, isScoreTransportLoss } from './RunScoreProof.js';
import { localVariantPlan, resolveLocalVariant } from './LocalVariants.js';
import { canSubmitLocalVariantScore, createLocalVariantGate } from './LocalVariantGate.js';
import {initPublicRanking,usesPublicRanking,stopPublicRanking} from './PublicRanking.js';
import {createStandaloneConnection} from './StandaloneConnection.js';

// Indexed by key, never by position — reordering the manifest must not silently
// re-point a handler.
export const ACTIONS = defineActions(manifest.liveActions);

// The gift grid. defineBindings validates the whole set at module load — band
// inside the field's schema bounds, no (tier ∩ band) overlap per group, choice
// options total and orthogonal — so a bad declaration fails in the dev server
// rather than after an upload. The assert keeps the committed manifest (the
// only thing the platform reads) equal to these declarations.
export const BINDINGS = defineBindings(ACTIONS, BINDING_DECLS);
try {
  BINDINGS.assertMatchesManifest(manifest.liveBindings);
} catch (e) {
  console.warn('[portal] manifest.liveBindings drifted from PortalBindings.js — re-run the sync in scripts/', e);
}

let session = null;
let shareReceipt = null;
export async function createAcceptedShareCard() {
  if (!session) return null;
  if (!shareReceipt) throw new Error('記録の受理を待ってから共有してください。');
  if (shareReceipt.claimRequired) throw new Error('記録は保存済みです。公開共有には「アカウント設定」でパスキー登録が必要です。');
  const card = await session.createShareCard({scoreSubmissionId:shareReceipt.scoreSubmissionId});
  return {...card, live:session.session?.rankingTrack === 'LIVE_INTERACTIVE'};
}
let initialised = false;
let localVariantHandle = null;
let localVariantLane = null;
const localVariantGate = createLocalVariantGate(runLocalVariantAfterTrustedStart);

// hex_sign state. The curse is a negated baseDamage; Formula.reset() restores
// +10 by itself on a new run, so the revert has to check the sign rather than
// blindly negate — a blind negate after a restart would re-arm the curse.
let hexUntil = 0;
let hexTicker = 0;

// One submission per run. The counter keys clientSubmissionId so a retry is a
// RETRY (same id) rather than a second spend of maxSubmissionsPerSession.
let runCounter = 0;
let submittedThisRun = false;
let standaloneMode = false;
let standaloneRankedRun = false;
const boardRefreshQueue = new PortalRefreshQueue();
const BOARD_REFRESH_DELAY_MS = 5500;
let boardRequestGeneration = 0;

export function initPortal() {
  if (initialised) return;
  initialised = true;

  // Fire-and-forget: connect() waits up to 10s for the platform's launch
  // message, and outside TRYPORTAL that is 10s of nothing. The game must never
  // wait on it.
  const localVariant = resolveLocalVariant();
  const localPlan = localVariantPlan(localVariant);
  localVariantHandle = localPlan ? Object.freeze({ ...localPlan }) : null;
  bus.on('ui:start', onRunStart);
  bus.on('ui:restart', onRunStart);
  bus.on('game:worldReady', onWorldReady);
  bus.on('game:worldNotReady', onWorldNotReady);
  bus.on('player:died', onDied);
  bus.on('enemy:death', onEnemyDeath);
  if(import.meta.env.VITE_SOCIAL_ENABLED==='true' && usesPublicRanking()) {
    standaloneMode=true;initPublicRanking();initStandaloneConnection();return;
  }

  // Always use the real platform SDK.  The local plan only changes the
  // deterministic enemy/weapon inputs used by the local acceptance lane; it cannot
  // manufacture a score, digest, receipt, board row or ticket.
  connect().then(attachSession)
    .catch((error) => {
      if (isTRYPORTALError(error) && error.code === 'NOT_LAUNCHED_BY_TRYPORTAL') {
        console.info('[portal] not launched by TRYPORTAL — running standalone');
      } else {
        console.warn('[portal] connect failed', error);
      }
    });
}

function attachSession(s) {
      session = s;
      if (localVariantHandle) localVariantGate.markSessionReady();
      if (localVariantHandle && typeof window !== 'undefined' && window.AXIOM) window.AXIOM.localVariants = localVariantHandle;
      // Stream mode first: it claims the declared tp_live_* actions, and
      // defineActions treats reserved keys as unconditionally optional, so the
      // order is not load-bearing — but claiming before registering keeps the
      // overlay alive from the first delivered event.
      try {
        s.enableStreamMode({
          // render:false keeps the parsing, the schema validation and the ACKs
          // and drops only the SDK's DOM rows. The game draws every viewer
          // event itself now, inside its own overlay and in Japanese; the
          // corner rows would print the same comment a second time in a second
          // visual language.
          render: false,
          // `actions` stays at its default — ALL of them. A kind this game has
          // no use for still gets claimed, so the platform receives a real ACK
          // for it instead of UNKNOWN_ACTION.
          onLike, onChat, onShare,
        });
      } catch (e) { console.warn('[portal] stream mode', e); }
      registerActions(s);
      console.info('[portal] connected', s.session?.rankingTrack ?? '');
      // The board this session is filed under. Exactly two reads per session —
      // here and after a score is accepted — because the shell answers repeats
      // from a short cache and then refuses (LEADERBOARD_UNAVAILABLE).
      void fetchBoard();
}

function initStandaloneConnection() {
  const panel=document.createElement('div');panel.className='ax-ranking-connect';
  const button=document.createElement('button');button.type='button';button.textContent='ランキングに接続';
  const status=document.createElement('span');status.setAttribute('role','status');
  const account=document.createElement('a');account.href='https://app.traident.jp/account/passkeys';account.target='_blank';account.rel='noopener noreferrer';account.textContent='公開順位のためのアカウント設定';account.hidden=true;
  status.textContent='接続後のプレイから記録を送信します';
  panel.append(button,status,account);document.body.append(panel);
  const connection=createStandaloneConnection({
    begin:()=>connectStandalone({gameKey:manifest.gameKey,scoreRetryAttempts:0}),
    ready:s=>{stopPublicRanking();attachSession(s);},
    changed:state=>{
      button.disabled=state==='connecting'||state==='connected';
      account.hidden=state!=='connected';
      button.textContent=state==='connecting'?'接続を確認中…':state==='connected'?'ランキング接続済み':'ランキングに接続';
      status.textContent=state==='connected'?'次のプレイから送信します。公開順位にはパスキー登録が必要です。':state==='failed'?'接続できませんでした。許可画面を確認して再試行してください。':'ゲーム画面はそのまま、別窓で接続を確認します';
    },
  });
  button.addEventListener('click',event=>{
    if(!event.isTrusted)return;
    void connection.request().catch(()=>{}); // Failure is rendered, never treated as accepted.
  });
  bus.on('state:phase',({phase})=>{panel.hidden=!['menu','dead'].includes(phase);});
}

// Game.js adds this to the read-only AXIOM QA surface after boot. Returning null
// keeps the hosted/public surface completely inert when the exact loopback
// launch shape is absent; no score or transport controls are exposed here.
export function getLocalVariantHandle() {
  return localVariantHandle;
}

function onRunStart() {
  standaloneRankedRun=standaloneMode && !!session;
  shareReceipt = null;
  runCounter++;
  submittedThisRun = false;
  bus.emit('portal:score', { state: 'idle' });
  clearHex();
  // A new run starts the gauge at zero and the field with no names on it.
  // Carrying either across a restart would credit this run with the last one's
  // audience.
  likeTotal = 0;
  // Only when a session exists. Emitting unconditionally built the gauge and
  // the free-action list for a SOLO player — a bar counting toward a threshold
  // no one can move, and a price list for a stream that is not happening. The
  // overlay treats any portal:like as proof of a live session, correctly; the
  // proof just has to be true.
  if (session) emitLike(0);
  clearTags();
  // A signed local-variant launch remains an ordinary visible start. The
  // deterministic plan is consumed only after ui:start and PLAYING; no URL
  // driven boot click or hidden pre-start run is allowed.
  if (localVariantHandle) {
    if (!localVariantGate.started) {
      localVariantLane = {
        runCounter, state: 'waiting-world', target: null, enemyDeathSeen: false,
        killsBefore: Number(S.state?.kills || 0), killsAfter: null,
      };
    }
    localVariantGate.markTrustedStart();
  }
}

function onWorldReady(world) {
  if (!localVariantHandle) return;
  console.info('[portal] local variant: world ready');
  localVariantGate.markWorldReady();
}

function onWorldNotReady(world) {
  if (!localVariantHandle || localVariantGate.started) return;
  const reasons = Array.isArray(world?.missing) ? world.missing.join(',') : 'unknown';
  // This is fail-closed diagnostic evidence, not a retry clock. A later
  // PLAYING transition (for example closing first-run Controls) schedules a
  // fresh Game-owned world check.
  console.info('[portal] local variant: world not ready (' + reasons + ')');
}

// ---------------------------------------------------------------- actions ---

function registerActions(s) {
  // The game's own veto, for state only it knows: outside a live run the world
  // systems the actions poke do not exist, and a queued interference landing on
  // the death screen would be indistinguishable from a bug. COSMETIC actions
  // (prime rain, the stream overlay's events) stay welcome everywhere.
  s.addGuard((envelope) => {
    const phase = S.state?.phase;
    if (phase !== 'playing' && envelope.safetyClass !== 'COSMETIC') {
      return { allow: false, reason: 'GAME_STATE_BLOCKED', detail: String(phase) };
    }
    return { allow: true };
  });

  ACTIONS.register(s, {
    spawn_wave: spawnWave,
    hex_sign: hexSign,
    gift_token: giftToken,
    drop_supply: dropSupply,
    prime_rain: primeRain,
  });
}

const clampInt = (v, lo, hi) => Math.max(lo, Math.min(hi, Math.round(Number(v) || lo)));

function spawnWave({ count, side }) {
  const sp = S.spawner, cam = S.camera;
  if (!sp || !cam) return;
  const want = clampInt(count, 1, 5);
  // Hard cap regardless of what the rule asked for: the wave director already
  // tuned concurrentCap to what the arena and the frame budget can hold.
  const room = Math.max(0, (sp.concurrentCap || 24) + 6 - sp.active.length);
  const n = Math.min(want, room);

  // Perpendicular to the view direction, so "left" is the player's left.
  const fwd = cam.getWorldDirection(_v3a).setY(0).normalize();
  const right = _v3b.set(-fwd.z, 0, fwd.x);
  for (let i = 0; i < n; i++) {
    const dir = side === 'left' ? -1 : side === 'right' ? 1 : (i % 2 ? 1 : -1);
    const p = cam.position.clone()
      .addScaledVector(right, dir * (10 + (i % 3) * 2))
      .addScaledVector(fwd, 4 + (i % 2) * 3);
    p.y = 0;
    // POSITION FIRST, wave second — forceSpawn(typeId, position, wave). The two
    // were the other way round here from the day this file was written, and the
    // mistake is silent by construction: reset() marks the body alive and in
    // use either way, so the count was right and the toast was right, while
    // `position.copy(<a number>)` left every viewer's reinforcement at NaN —
    // unhittable, unseeable, and still counted against the concurrency cap that
    // decides when real enemies may spawn. The paid half of the grid was buying
    // an enemy nobody could fight.
    sp.forceSpawn('grunt', p, S.state.wave || 1);
  }
  if (n > 0) {
    bus.emit('sfx', { name: 'portal', volume: 0.8 });
    bus.emit('portal:toast', { text: `視聴者の増援 ×${n}`, tone: 'harm' });
  }
}

function hexSign({ durationMs }) {
  const f = S.formula;
  if (!f || !f.baseDamage) return;
  const d = clampInt(durationMs, 3000, 8000);
  if (!f.baseDamage.isNeg()) {
    f.baseDamage = f.baseDamage.neg();
    f._changed();
  }
  hexUntil = performance.now() + d;      // a second curse REPLACES the deadline
  bus.emit('portal:hex', { until: hexUntil, active: true });
  bus.emit('sfx', { name: 'ui_deny', volume: 0.8, pitch: 0.6 });
  bus.emit('portal:toast', { text: '符号の呪い — 共役(N)で解除', tone: 'harm' });
  if (!hexTicker) hexTicker = setInterval(tickHex, 250);
}

function tickHex() {
  if (performance.now() < hexUntil) return;
  clearHex();
}

function clearHex() {
  if (hexTicker) { clearInterval(hexTicker); hexTicker = 0; }
  const f = S.formula;
  if (hexUntil && f && f.baseDamage && f.baseDamage.isNeg()) {
    f.baseDamage = f.baseDamage.neg();
    f._changed();
  }
  if (hexUntil) bus.emit('portal:hex', { active: false });
  hexUntil = 0;
}

function giftToken({ tier }) {
  const loot = S.loot, cam = S.camera;
  if (!loot || !cam) return;
  const t = clampInt(tier, 1, 3);
  // Statically imported, not import()'d. A dynamic import on the action path
  // costs a chunk fetch the first time, and a headless run measured the crate
  // landing AFTER the 450ms window — one whole test later. The design rule is
  // that the change must BEGIN within 500ms of the handler, so an effect that
  // waits on a module load fails it. Both modules are in the main bundle
  // already, so hoisting them costs nothing.
  //
  // rollToken biases its tier table by wave; the payload tier shifts it so
  // tier 1 stays entry-level arithmetic and tier 3 reaches the ^/! shelf.
  const bias = t === 1 ? -2 : t === 2 ? 0 : 3;
  const tok = rollToken(rand, S.state.wave || 1, bias);
  if (!tok) return;
  // Outside MAGNET_RADIUS (4 m), deliberately. At 3 m the crate was inside the
  // magnet and vanished into the formula on the next frame — a gift the viewer
  // paid for that nobody could see arrive. At 7 m it lands, glows, and the
  // player walks to it: the effect is visible WHILE it travels, which is the
  // one legitimate way an action passes the "begins within 500 ms" test with a
  // delayed payoff.
  const fwd = cam.getWorldDirection(_v3a).setY(0).normalize();
  const p = cam.position.clone().addScaledVector(fwd, 7);
  p.y = 0.1;
  loot.spawn(tok, p);
  bus.emit('sfx', { name: 'pickup', volume: 0.8, pitch: 1.2 });
  bus.emit('portal:toast', { text: '視聴者から演算子の差し入れ', tone: 'help' });
}

function dropSupply({ kind }) {
  const p = S.player;
  if (!p) return;
  if (kind === 'ammo') {
    const w = p.weapon;
    if (w && w.def) w.reserve = w.def.reserve;
    bus.emit('sfx', { name: 'reload_in', volume: 0.9 });
    bus.emit('portal:toast', { text: '視聴者から弾薬補給', tone: 'help' });
  } else {
    p.hp = Math.min(p.maxHp, p.hp + p.maxHp * 0.35);
    bus.emit('sfx', { name: 'pickup', volume: 0.9 });
    bus.emit('portal:toast', { text: '視聴者から回復 +35%', tone: 'help' });
  }
}

function primeRain({ density }) {
  bus.emit('portal:rain', { density: clampInt(density, 1, 3) });
  bus.emit('sfx', { name: 'prime', volume: 0.7 });
}

// -------------------------------------------------------------- free tier ---
// Likes, comments and shares cost the viewer nothing, and almost nobody who
// sends one can send a gift. They are still the whole top of the funnel: they
// buy the impressions, the impressions fill the room, and a full room is what
// makes a big gift worth throwing.
//
// The bindings already turn all three into rain server-side. What was missing
// is the RECEIPT for the free act itself — a gauge that visibly moves, a
// comment that reaches the world, an acknowledgement of a share that happened
// somewhere off-screen. A free action whose result nobody can point at is done
// once and never again.
//
// Everything below is emitted, never drawn. The overlay owns the pixels; this
// file owns the guarantee that what reaches it is already guarded.

// Read from the binding, not typed twice. The gauge and the server must never
// disagree about when the rain is due — a bar that fills without raining reads
// as the game ignoring the room, which is the exact opposite of the point.
const LIKE_THRESHOLD = (() => {
  const n = Math.round(Number(BINDING_DECLS?.like_rain?.trigger?.likeThreshold));
  return Number.isFinite(n) && n > 0 ? n : 50;
})();

let likeTotal = 0;

// LiveLike.count is the taps in ONE batch as the provider coalesced them, never
// a running total (invariant 21) — so the total is ours to keep.
function onLike(like) {
  const n = Math.max(1, Math.round(Number(like?.count) || 1));
  likeTotal += n;
  // Subtract, never zero: a 60-like batch has paid for one rain and the
  // remaining 10 belong to the next one. Zeroing would quietly rob every
  // viewer whose tap landed in the same batch as the crossing.
  //
  // A loop, because one coalesced batch can cross the line several times, and a
  // gauge sitting at 150/50 is a gauge nobody believes.
  while (likeTotal >= LIKE_THRESHOLD) likeTotal -= LIKE_THRESHOLD;
  emitLike(n);
}

// The gauge only reports. The rain itself is the server's binding — firing one
// here as well would double every crossing.
function emitLike(count) {
  bus.emit('portal:like', {
    count,
    total: likeTotal,
    threshold: LIKE_THRESHOLD,
    remaining: LIKE_THRESHOLD - likeTotal,
  });
}

// Long enough that a busy chat cannot turn the arena into name soup, short
// enough that someone who commented can still reasonably expect to see
// themselves out there.
const TAG_COOLDOWN_MS = 12000;
const MAX_TAGS = 8;

// enemy -> the GUARDED label. Strong references are fine and deliberate: the
// map is capped at MAX_TAGS, every path that ends an enemy's life drops its
// entry, and the enemies are pooled objects that outlive this map anyway.
const tags = new Map();
let tagCooldownUntil = 0;

function onChat(chat) {
  // The platform moderated these before they were sent. guardText is the other
  // half and a different question: moderation answers "should this be shown",
  // this answers "can this string do anything other than be read". Both strings
  // are guarded HERE, at the last moment before they leave this file, so
  // nothing downstream is ever handed the raw text.
  const label = guardText(chat?.label, { maxLength: 16 });
  const text = guardText(chat?.text, { maxLength: 60 });
  // Empty means nothing legible survived. An unattributed bubble, or a named
  // empty one, is still something the whole audience has to look at.
  if (!label || !text) return;

  // The presence receipt, unconditionally: every comment reaches the world even
  // when the cooldown means this one does not get to name a body.
  bus.emit('portal:chat', { label, text });
  tagOne(label);
}

function tagOne(label) {
  // Outside a live run there is nothing to name, and a nameplate on the death
  // screen belongs to a fight nobody is having.
  if (S.state?.phase !== 'playing') return;
  const now = performance.now();
  if (now < tagCooldownUntil) return;
  pruneTags();
  if (tags.size >= MAX_TAGS) return;

  const list = S.spawner?.active;
  if (!list || list.length === 0) return;      // nothing alive — skip silently

  // Never spawn for a comment. A chat that suddenly got busy would be a
  // difficulty spike the player never agreed to, and the free tier must never
  // be the thing that kills them. Name a body that is already on the field.
  //
  // Nearest to the camera, because a name the audience cannot read is not a
  // receipt — it is a label on something across the arena.
  const cam = S.camera;
  let best = null;
  let bestD = Infinity;
  for (const e of list) {
    if (!e || !e.alive || !e.inUse || tags.has(e)) continue;
    const d = cam ? cam.position.distanceToSquared(e.position) : 0;
    // A body whose position is not a number must not win the comparison — and
    // must not be able to veto a healthy one either. Every comparison against
    // NaN is false, so a bare `d < bestD` over a field of broken bodies elects
    // nobody and the tag quietly never happens. That is not hypothetical: it is
    // how the swapped forceSpawn arguments above showed themselves, as a
    // feature that did nothing rather than as an error.
    if (!Number.isFinite(d)) { best ??= e; continue; }
    if (d < bestD) { bestD = d; best = e; }
  }
  if (!best) return;

  tags.set(best, label);
  tagCooldownUntil = now + TAG_COOLDOWN_MS;
  bus.emit('portal:tag', { enemy: best, label });
}

// Death is not the only way an enemy leaves the field: a restart despawns
// everything wholesale, and the pool hands the same object back later. An entry
// that outlived its body would put a stranger's name on whoever occupies it
// next.
function pruneTags() {
  for (const [enemy] of tags) {
    if (enemy && enemy.alive && enemy.inUse) continue;
    tags.delete(enemy);
    bus.emit('portal:untag', { enemy });
  }
}

function clearTags() {
  for (const [enemy] of tags) bus.emit('portal:untag', { enemy });
  tags.clear();
  tagCooldownUntil = 0;
}

// Every kill in the run passes through here, so it does one Map lookup on a map
// of at most eight and leaves.
function onEnemyDeath(evt) {
  const enemy = evt?.enemy;
  if (!enemy) return;
  if (localVariantLane?.target === enemy && localVariantLane.runCounter === runCounter) {
    // Enemy.kill() emitted this from the real Weapon._damageEnemy route. Game
    // increments the run counter later in the same synchronous event fan-out,
    // so record the event here and read the final count on the staged death.
    localVariantLane.enemyDeathSeen = true;
    localVariantLane.state = 'weapon-killed';
  }
  const label = tags.get(enemy);
  if (!label) return;
  tags.delete(enemy);
  bus.emit('portal:untag', { enemy });
  // Already guarded — it was never stored any other way.
  bus.emit('portal:toast', { text: `${label} を撃破`, tone: 'help' });
}

function onShare() {
  // The rain is the binding's. This is the attribution: a share happens
  // entirely off-screen, so the screen is the only place it can be answered.
  bus.emit('portal:toast', { text: 'シェアありがとう', tone: 'help' });
}

// ------------------------------------------------------------------ score ---

async function onDied() {
  clearHex();
  if (submittedThisRun) return;
  // A connection completed during a run does not retroactively rank that run.
  if (standaloneMode && !standaloneRankedRun) return;
  if (localVariantHandle && !canSubmitLocalVariantScore(localVariantLane, runCounter)) {
    // Do not turn a missed world-ready signal, a failed spawn, or a natural
    // death into a legitimate-looking zero. This restriction exists only for
    // the signed loopback acceptance lane; ordinary/public runs keep their
    // existing score semantics.
    submittedThisRun = true;
    const state = localVariantLane?.state || 'not-started';
    console.warn('[portal] local variant: incomplete run; score submit blocked (' + state + ')');
    bus.emit('portal:score', { state: 'failed', reason: 'local-variant-incomplete' });
    return;
  }
  if (!session) return;
  submittedThisRun = true;
  // Bind ownership before the asynchronous digest. Retry can begin another run
  // while that digest or the network is pending; its result must not become
  // the new run's share receipt or overwrite that run's submission state.
  const sourceRun = runCounter;
  const sourceSession = session;
  const isCurrentRun = () => sourceRun === runCounter && sourceSession === session;
  const emitScore = payload => { if (isCurrentRun()) bus.emit('portal:score', payload); };

  // Freeze one proof before touching the transport. Every retry reuses this
  // exact object and UUID; a second snapshot could disagree with the final
  // frame and a new id could spend another session submission slot.
  let runProof;
  try {
    runProof = await createRunScoreProof(S.state);
  } catch (error) {
    // Digest failure is deliberately fail-closed: there is no score submission,
    // local fallback, or made-up digest that could masquerade as trusted proof.
    console.warn('[portal] score proof unavailable', error);
    emitScore({ state: 'failed', reason: 'proof-unavailable' });
    return;
  }

  const { score: magnitude, stateDigest, clientSubmissionId, proof } = runProof;
  if (localVariantHandle && (!Number.isSafeInteger(magnitude) || magnitude <= 0)) {
    console.warn('[portal] local variant: non-positive proof score; score submit blocked');
    emitScore({ state: 'failed', reason: 'local-variant-proof-invalid' });
    return;
  }
  const attempt = () => sourceSession.submitScore({
    score: magnitude,
    final: true,
    durationMs: proof.durationMs,
    stateDigest,
    clientSubmissionId,
    custom: { runScoreProof: proof },
  });

  emitScore({ state: 'sending', magnitude, clientSubmissionId });

  const accepted = (receipt) => {
    if (isCurrentRun()) shareReceipt = receipt.scoreSubmissionId ? Object.freeze({scoreSubmissionId:receipt.scoreSubmissionId,claimRequired:receipt.claimRequired===true}) : null;
    console.info('[portal] score accepted', magnitude, receipt.verificationStatus,
      receipt.claimRequired ? '(claim required)' : '');
    emitScore({
      state: 'accepted', magnitude,
      verificationStatus: receipt.verificationStatus,
      publicRankEligible: receipt.publicRankEligible,
      claimRequired: receipt.claimRequired,
    });
    if (isCurrentRun()) bus.emit('portal:toast', {
      text: receipt.claimRequired ? '記録を保存しました' : `記録を受理 — ${magnitude}桁`,
      tone: 'info',
    });
    // The hosted relay caches a board window for five seconds and projection is
    // asynchronous. Refresh once beyond that window; never poll or claim a row
    // moved until the trusted board says so.
    boardRefreshQueue.schedule(BOARD_REFRESH_DELAY_MS, () => {
      fetchBoard({ afterScore: magnitude, claimRequired: receipt.claimRequired, runCounter:sourceRun, session:sourceSession });
    });
  };

  try {
    const receipt = await attempt();
    accepted(receipt);
  } catch (error) {
    let finalError = error;
    // Same clientSubmissionId on the retry: a new one would spend another of
    // the session's maxSubmissionsPerSession on the same run.
    if (isScoreTransportLoss(error)) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const receipt = await attempt();
        accepted(receipt);
        return;
      } catch (e2) { finalError = e2; console.warn('[portal] score retry failed', e2); }
    } else {
      console.warn('[portal] score refused', error);
    }
    const state = isTRYPORTALError(finalError) && finalError.code === 'SCORE_REJECTED'
      ? 'rejected' : 'failed';
    emitScore({ state, magnitude });
    if (isCurrentRun()) bus.emit('portal:toast', { text: '記録を送信できませんでした', tone: 'info' });
  }
}

// ------------------------------------------------------------------ board ---

async function fetchBoard(context = null) {
  const readSession = context?.session || session;
  const readRun = context?.runCounter ?? runCounter;
  if (!readSession) return;
  const requestGeneration = ++boardRequestGeneration;
  if (!context) bus.emit('portal:score', { state: 'loading' });
  try {
    const board = await readSession.leaderboard({ limit: 20 });
    if(readSession !== session || requestGeneration !== boardRequestGeneration)return;
    // myEntry === null is a SUCCESS: a guest's verified score stays off the
    // public board until the account gains a passkey. Render the board and let
    // the overlay say so, rather than treating it as an error.
    bus.emit('portal:board', {
      entries: board.entries, myEntry: board.myEntry,
      source: readSession.transportKind === 'DIRECT' ? 'TRAIDENT_DIRECT' : 'TRAIDENT_HOSTED',
      track: board.rankingTrack, refreshedAfterScore: !!context,
    });
    if (context && readRun === runCounter) {
      const myScore = board.myEntry ? Number(board.myEntry.scoreValue) : null;
      bus.emit('portal:score', {
        state: 'refreshed', magnitude: context.afterScore,
        claimRequired: context.claimRequired,
        myScore,
      });
    } else if (!context && readRun === runCounter) {
      bus.emit('portal:score', { state: 'idle' });
    }
  } catch (e) {
    if(readSession !== session || requestGeneration !== boardRequestGeneration || readRun !== runCounter)return;
    // LEADERBOARD_UNAVAILABLE past the allowance is expected; the overlay just
    // keeps whatever it last had.
    console.info('[portal] leaderboard unavailable', e?.code || e);
    if (context) bus.emit('portal:score', {
      state: 'refresh-failed', magnitude: context.afterScore,
      claimRequired: context.claimRequired,
    });
    else {
      bus.emit('portal:board', { state: 'unavailable', entries: [], myEntry: null, source:session.transportKind === 'DIRECT' ? 'TRAIDENT_DIRECT' : 'TRAIDENT_HOSTED' });
      bus.emit('portal:score', { state: 'unavailable' });
    }
  }
}

// ---------------------------------------------------------- local variants ---

// A signed local-variant launch is driven by the same visible start control as
// every other run. Game has already provided a next-turn world-ready event;
// this function therefore has no boot-time polling or wall-clock expiry.
function runLocalVariantAfterTrustedStart() {
  const log = (m) => console.log('[portal] local variant:', m);
  const plan = localVariantHandle;
  const worldStillReady = S.state?.phase === 'playing' && S.spawner && S.camera
    && S.player?.weapon && !S.controls?.open;
  if (!plan || !worldStillReady) {
    console.warn('[portal] local variant: refused staging because world-ready invariant changed');
    return;
  }
  // Local acceptance keeps the game audio muted in its test session.
  try { S.audio?.setMasterVolume?.(0); } catch (e) { /* not built yet */ }
  log('world ready — staging kill');
  const fwd = S.camera.getWorldDirection(_v3a).setY(0).normalize();
  const p = S.camera.position.clone().addScaledVector(fwd, 8); p.y = 0;
  // The plan supplies only ordinary engine inputs. AXIOM's formal profiles
  // deliberately request exactly one target: any other shape fails closed
  // instead of silently changing the one-kill acceptance contract.
  if (plan.enemyCount !== 1) {
    localVariantLane = { runCounter, state: 'failed-plan-enemy-count', target: null, enemyDeathSeen: false, killsBefore: Number(S.state?.kills || 0), killsAfter: null };
    console.warn('[portal] local variant: unsupported enemy count; refusing score run');
    return;
  }
  const e = S.spawner.forceSpawn('grunt', p, 1);   // (typeId, position, wave)
  if (!e) {
    localVariantLane = { runCounter, state: 'failed-spawn', target: null, enemyDeathSeen: false, killsBefore: Number(S.state?.kills || 0), killsAfter: null };
    console.warn('[portal] local variant: forceSpawn failed; refusing score run');
    return;
  }
  localVariantLane = {
    runCounter, state: 'damaging', target: e, enemyDeathSeen: false,
    killsBefore: Number(S.state?.kills || 0), killsAfter: null,
  };
  setTimeout(() => {
        if (S.state?.phase !== 'playing' || !e.alive) {
          localVariantLane.state = 'failed-target-unavailable';
          console.warn('[portal] local variant: target unavailable; refusing score run');
          return;
        }
        // Before the damage, while the grunt is certainly still alive: the
        // comment needs a body to name, and the kill below then carries the
        // tag through untag and the 撃破 toast in the same breath.
        // Through the real weapon, not kill(false): the whole point of the
        // score is that it travelled the damage pipeline, so the board
        // receives a genuine magnitude rather than a bypassed 0.
        if (plan?.weaponIndex != null && S.player?.switchWeapon) S.player.switchWeapon(plan.weaponIndex);
        const activeWeapon = S.player?.weapon;
        const hitCount = plan.weaponHits ?? 6;
        if (e && e.alive && activeWeapon && typeof activeWeapon._damageEnemy === 'function') {
          const hit = {
            enemy: e,
            // Headshot is an ordinary weapon hit condition, not a score
            // override.  It lets the real formula/damage pipeline produce the
            // challenger/self magnitude from the same deterministic target.
            point: e.position.clone().setY(plan?.headshot ? 1.65 : 1),
            normal: fwd.clone().negate(), distance: 8, headshot: false,
          };
          for (let i = 0; i < hitCount && e.alive; i++) activeWeapon._damageEnemy({ ...hit, headshot: !!plan?.headshot }, fwd, 1);
          log('damaged through the weapon; peak=' + (S.state?.peakDamage?.logAbs10?.() ?? 'null'));
        }
        if (e.alive) {
          console.warn('[portal] local variant: weapon did not kill target; refusing score run');
          localVariantLane.state = 'failed-weapon-lethality';
          return;
        }
        // A window before dying, so a human (or a harness) can press live-action
        // buttons against a RUNNING game and observe APPLIED acks.
        log('kill staged; dying in 8s');
        setTimeout(() => {
          if (S.state?.phase !== 'playing') return;
          if (!localVariantLane?.enemyDeathSeen || Number(S.state?.kills || 0) <= localVariantLane.killsBefore) {
            localVariantLane.state = 'failed-kill-observation';
            console.warn('[portal] local variant: enemy death was not reflected in kills; refusing score run');
            return;
          }
          localVariantLane.killsAfter = Number(S.state.kills);
          localVariantLane.state = 'completed';
          S.player?.takeDamage?.(99999, null);
          log('death requested');
        }, plan.deathDelayMs ?? 8000);
      }, 600);
}

// Scratch vectors — the handlers run at action rate, not frame rate, but the
// codebase's convention is to never allocate in anything the bus can reach.
import * as THREE from 'three';
const _v3a = new THREE.Vector3();
const _v3b = new THREE.Vector3();
